/**
 * Client HTTP du back-office.
 *
 * Il est écrit CONTRE `docs/API.md`, pas contre le serveur : le contrat a été
 * rédigé avant le code, exactement pour que le dashboard puisse être terminé
 * sans attendre l'API. Tant que le serveur n'est pas là, chaque appel échoue
 * proprement en `ApiError` et l'écran affiche un état lisible.
 *
 * Deux niveaux de rigueur sur les types :
 *  - là où `API.md` donne la forme exacte de la réponse (overview, timeseries,
 *    breakdown), on type directement ;
 *  - là où le contrat reste en une ligne (réseau, entonnoir, incidents,
 *    rapports), on passe par un **normaliseur défensif** qui part de `unknown`.
 *    Une réponse inattendue produit alors une section vide, jamais un écran
 *    blanc dû à un `undefined.map`.
 */

const BASE = '/api/v1';
/** En-tête posé sur chaque écriture — même nom que `server/src/lib/csrf.ts`. */
const DASHBOARD_HEADER = 'x-yog-dashboard';

// ─── Session d'opérateur (O-3) ──────────────────────────────────────────────

/**
 * Il n'y a plus RIEN à stocker côté navigateur.
 *
 * L'ancien jeton partagé vivait en `localStorage`, donc lisible par n'importe
 * quel script de la page. La session est maintenant un cookie `HttpOnly` que le
 * JavaScript ne peut ni lire ni écrire : le navigateur l'attache tout seul,
 * à condition que chaque requête parte en `credentials: 'include'`.
 *
 * Conséquence pratique : `readToken`/`writeToken` ont disparu, et l'état
 * « suis-je connecté ? » ne se déduit plus d'un stockage local — il se demande
 * au serveur, par `GET /auth/me`. C'est une source de vérité de moins à tenir
 * synchronisée, et un secret de moins exposé à une XSS.
 */

export const ROLES = ['viewer', 'operator', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'read',
  'incidents:write',
  // Acquitter une alerte de seuil (O-4). Doit rester ALIGNÉE sur
  // `server/src/lib/roles.ts` : `normalizeSession` filtre les permissions
  // inconnues, donc une permission oubliée ici griserait le bouton
  // d'acquittement pour tout le monde, y compris pour un administrateur.
  'alerts:ack',
  'reports:generate',
  // Déposer, corriger et publier le plan de départ vers l'aéroport (2026-10-07).
  'departures:publish',
  // Envoyer une annonce sur les téléphones (2026-10-22) : administrateur seulement.
  'announcements:send',
  'jobs:run',
  'operators:manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export interface OperatorIdentity {
  id: string;
  username: string;
  displayName: string;
  role: Role;
}

export interface SessionInfo {
  operator: OperatorIdentity;
  permissions: Permission[];
}

/** Comptes d'exploitation (page « Comptes », réservée aux administrateurs). */
export interface OperatorAccount {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  createdAt: string;
  lastLoginAt: string | null;
  disabledAt: string | null;
}

/** Longueur minimale d'un mot de passe — même valeur que `server/src/lib/password.ts`. */
export const MIN_PASSWORD_LENGTH = 12;

export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'Lecture seule',
  operator: 'Opérateur TROC',
  admin: 'Administrateur',
};

/**
 * Événement émis quand le serveur répond 401 sur une route protégée.
 *
 * Un `CustomEvent` plutôt qu'un état React : le 401 peut survenir dans
 * n'importe quel appel, y compris un rafraîchissement automatique déclenché par
 * une page que l'opérateur ne regarde même pas. Faire remonter ça par les props
 * obligerait chaque page à savoir gérer la déconnexion ; ici, `App` écoute une
 * fois et personne d'autre ne s'en occupe.
 */
export const UNAUTHENTICATED_EVENT = 'yog:unauthenticated';

// ─── Erreurs ────────────────────────────────────────────────────────────────

/** Problem Details RFC 9457 — même format que la passerelle SAEIV. */
export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  /** `true` quand la requête n'a même pas abouti (API éteinte, DNS, CORS). */
  readonly offline: boolean;

  constructor(status: number, problem: Problem, offline = false) {
    super(problem.title ?? problem.detail ?? `Erreur HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.offline = offline;
  }

  /** Message destiné à l'écran, en français, sans jargon HTTP inutile. */
  get userMessage(): string {
    if (this.offline) return "L'API du back-office ne répond pas.";
    if (this.status === 401) return 'Session expirée : reconnectez-vous.';
    // 403 ≠ 401 : l'opérateur est bien identifié, c'est son rôle qui ne suffit
    // pas. Le renvoyer à l'écran de connexion ne changerait rien à son problème.
    if (this.status === 403) return 'Votre rôle ne permet pas cette action.';
    if (this.status === 404) return "Cette ressource n'existe pas sur le serveur.";
    if (this.status === 429) return 'Trop de requêtes : patientez quelques secondes.';
    if (this.status >= 500) return 'Le serveur a rencontré une erreur interne.';
    return this.problem.title ?? `Erreur ${this.status}`;
  }
}

// ─── Transport ──────────────────────────────────────────────────────────────

type Query = Record<string, string | number | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
  }
  const suffix = qs.toString();
  return `${BASE}${path}${suffix ? `?${suffix}` : ''}`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** `text` sert aux exports Markdown / CSV, qui ne sont pas du JSON. */
  as?: 'json' | 'text';
}

async function toProblem(res: Response): Promise<{ problem: Problem; parsed: boolean }> {
  try {
    const body: unknown = await res.json();
    if (body !== null && typeof body === 'object') return { problem: body as Problem, parsed: true };
  } catch {
    /* corps non-JSON : proxy sans cible, page d'erreur HTML d'un reverse proxy… */
  }
  return { problem: { status: res.status, title: `Erreur HTTP ${res.status}` }, parsed: false };
}

interface RequestFlags {
  /**
   * Coupe la notification de déconnexion. Réservé aux routes `/auth/*` : un 401
   * sur `POST /auth/login` signifie « mot de passe faux », pas « session
   * expirée », et un 401 sur `GET /auth/me` est la réponse normale quand
   * personne n'est connecté. Les propager déclencherait une boucle de
   * déconnexion sur l'écran de connexion lui-même.
   */
  silent401?: boolean;
}

async function request<T>(path: string, opts: RequestOptions & RequestFlags = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  // Signature du tableau de bord sur chaque écriture : le serveur refuse une
  // modification qui ne la porte pas (protection contre les requêtes forgées
  // depuis un autre site — un formulaire tiers ne peut pas poser cet en-tête).
  if ((opts.method ?? 'GET') !== 'GET') headers[DASHBOARD_HEADER] = '1';

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      // Sans ça, le cookie `HttpOnly` de session ne partirait tout simplement
      // pas et toute la surface admin répondrait 401.
      credentials: 'include',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (cause) {
    // `AbortError` n'est pas une panne : on le laisse remonter tel quel pour que
    // le hook sache qu'il s'agit d'un simple changement de période.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, { title: 'API injoignable', detail: String(cause) }, true);
  }

  if (!res.ok) {
    const { problem, parsed } = await toProblem(res);
    if (res.status === 401 && opts.silent401 !== true) {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    }
    // Le proxy de développement répond « 500 text/plain » quand rien n'écoute
    // sur le port 4000. Ce n'est pas une erreur applicative mais une API
    // absente : le message doit envoyer l'opérateur démarrer le serveur, pas
    // chercher un bug côté back-office.
    throw new ApiError(res.status, problem, !parsed && res.status >= 500);
  }
  if (res.status === 204) return undefined as T;
  if (opts.as === 'text') return (await res.text()) as T;

  try {
    return (await res.json()) as T;
  } catch (cause) {
    throw new ApiError(res.status, { title: 'Réponse illisible', detail: String(cause) });
  }
}

// ─── Petits utilitaires de lecture défensive ────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function optStr(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}
function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function optNum(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

// ─── Types de lecture ───────────────────────────────────────────────────────

export interface Overview {
  range: { from: string; to: string };
  users: { total: number; new: number; active24h: number; active7d: number; active30d: number };
  /** Sessions ouvertes ayant émis un battement depuis moins de `windowMin` minutes. */
  live: { connected: number; asOf: string; windowMin: number };
  sessions: { count: number; medianDurationSec: number };
  retention: { d1: number; d7: number };
  fleet: {
    vehiclesOnline: number;
    linesCovered: number;
    /** `null` quand aucun instantané ne porte d'âge de point GPS. */
    oldestFixAgeSec: number | null;
    /** Champs de fraîcheur de l'INSTANTANÉ. Optionnels : un serveur antérieur ne les envoie pas. */
    capturedAt?: string | null;
    snapshotAgeSec?: number | null;
    staleAfterSec?: number;
    stale?: boolean;
  };
  incidents: { open: number; resolved24h: number };
}

export const TIMESERIES_METRICS = [
  'active_users',
  'new_users',
  'sessions',
  'connected',
  'events',
  'alerts_set',
  'alerts_fired',
  'fleet_online',
  'errors',
] as const;
export type TimeseriesMetric = (typeof TIMESERIES_METRICS)[number];

export type Bucket = 'hour' | 'day';

export interface TimePoint {
  t: string;
  v: number;
}
export interface Timeseries {
  metric: TimeseriesMetric;
  bucket: Bucket;
  points: TimePoint[];
}

export const BREAKDOWN_DIMENSIONS = [
  'platform',
  'app_version',
  'locale',
  'screen',
  'line',
  'event_name',
  'incident_category',
] as const;
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[number];

export interface BreakdownRow {
  key: string;
  label: string;
  value: number;
  share: number;
}
export interface Breakdown {
  dimension: BreakdownDimension;
  rows: BreakdownRow[];
}

export interface FunnelStep {
  /** Nom d'événement du catalogue fermé (`app_open`, `sign_in`…). */
  name: string;
  label: string;
  value: number;
  /** Taux de passage depuis l'étape précédente, 0→1. Absent sur la première. */
  rate: number | null;
}
export interface Funnel {
  steps: FunnelStep[];
}

export interface NetworkLine {
  lineNo: string;
  name: string;
  color?: string;
  cluster?: string;
  kind?: string;
  stops?: number;
  vehiclesOnline?: number;
  vehiclesExpected?: number;
  oldestFixAgeSec?: number;
}

export interface NetworkVehicle {
  id: string;
  lineNo: string;
  fixAgeSec: number;
  lat?: number;
  lon?: number;
  speedKmh?: number;
}

export interface LenzHealth {
  reachable: boolean;
  latencyMs?: number;
  lastSyncAt?: string;
  status?: string;
}

/**
 * Toute réponse « réseau » peut arriver indisponible : la passerelle Lenz est un
 * système tiers. On modélise ce cas dans le type plutôt que de le traiter comme
 * une erreur — un back-office qui affiche « erreur » quand Lenz tousse fait
 * croire à une panne du back-office.
 */
export type Maybe<T> = { available: true; value: T } | { available: false; reason?: string };

export interface Incident {
  id: string;
  source: 'app' | 'troc' | string;
  category: string;
  lineNo: string | null;
  severity: 'low' | 'medium' | 'high' | string;
  status: 'open' | 'ack' | 'resolved' | string;
  createdAt: string;
  resolvedAt: string | null;
  note: string | null;
}

/**
 * Alerte de seuil (O-4).
 *
 * `rule` est l'identifiant TECHNIQUE de la règle. Il ne s'affiche jamais tel
 * quel : `lib/alertCatalog.ts` en fait un titre, une explication et une
 * consigne en français. Le lecteur est un opérateur TROC, pas un développeur.
 */
export interface Alert {
  id: string;
  rule: string;
  severity: 'info' | 'warning' | 'critical' | string;
  /** Titre déjà rédigé en français par le serveur. */
  title: string;
  detail: string;
  openedAt: string;
  resolvedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  lastNotifiedAt: string | null;
}

export interface ReportSummary {
  id: string;
  kind: 'daily' | 'weekly' | 'custom' | string;
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
}

export interface ReportDetail extends ReportSummary {
  summaryMd: string;
  payload: unknown;
}

export interface Health {
  status: string;
  db: string;
  lenz: 'up' | 'degraded' | 'down' | string;
  uptimeSec: number;
  version: string;
}

// ─── Normaliseurs ───────────────────────────────────────────────────────────

/**
 * Libellés français des étapes de l'entonnoir, si le serveur n'en fournit pas.
 *
 * Couvre volontairement plus d'événements que les quatre étapes actuelles : le
 * serveur est libre de changer `FUNNEL_STEPS` sans redéploiement du dashboard,
 * et une étape sans libellé s'afficherait sous son nom technique (`bus_tap`) au
 * milieu de libellés français. `sign_in` reste listé bien qu'il ne soit plus une
 * étape — il figure ailleurs, et le retirer d'ici ne gagnerait rien.
 */
const FUNNEL_LABELS: Record<string, string> = {
  app_open: "Ouverture de l'app",
  sign_in: 'Saisie ACR validée',
  map_open: 'Ouverture de la carte',
  line_view: "Consultation d'une ligne",
  schedule_view: 'Consultation des horaires',
  bus_tap: 'Appui sur un bus',
  alert_set: 'Alerte posée',
  alert_fired: 'Alerte déclenchée',
};

function normalizeFunnel(raw: unknown): Funnel {
  const source = isRecord(raw) ? arr(raw['steps']) : arr(raw);
  const steps: FunnelStep[] = source.filter(isRecord).map((row) => {
    const name = str(row['name'] ?? row['step'] ?? row['key'], 'sans nom');
    return {
      name,
      label: str(row['label'], FUNNEL_LABELS[name] ?? name),
      value: num(row['value'] ?? row['count']),
      rate: optNum(row['rate'] ?? row['conversion']) ?? null,
    };
  });

  // Le contrat ne garantit pas que le taux de passage soit calculé côté serveur.
  // On le dérive quand il manque : mieux vaut un pourcentage cohérent qu'un tiret.
  for (let i = 1; i < steps.length; i += 1) {
    const step = steps[i];
    const prev = steps[i - 1];
    if (!step || !prev) continue;
    if (step.rate === null) step.rate = prev.value > 0 ? step.value / prev.value : 0;
  }
  return { steps };
}

/**
 * Reconnaît le refus explicite `{ available: false }` demandé par le contrat,
 * et le distingue d'une réponse vide (0 véhicule est une information, pas une
 * indisponibilité).
 */
function unwrapAvailability(raw: unknown): { ok: boolean; reason?: string; body: unknown } {
  if (isRecord(raw) && raw['available'] === false) {
    return { ok: false, reason: optStr(raw['reason'] ?? raw['detail'] ?? raw['title']), body: null };
  }
  return { ok: true, body: raw };
}

function normalizeLines(raw: unknown): Maybe<NetworkLine[]> {
  const { ok, reason, body } = unwrapAvailability(raw);
  if (!ok) return { available: false, reason };
  const source = isRecord(body) ? arr(body['lines'] ?? body['rows'] ?? body['items']) : arr(body);
  const value: NetworkLine[] = source.filter(isRecord).map((row) => ({
    lineNo: str(row['lineNo'] ?? row['id'], '?'),
    name: str(row['name'] ?? row['label'], str(row['lineNo'], 'sans nom')),
    color: optStr(row['color']),
    cluster: optStr(row['cluster']),
    kind: optStr(row['kind']),
    stops: optNum(row['stops'] ?? row['stopCount']),
    vehiclesOnline: optNum(row['vehiclesOnline']),
    vehiclesExpected: optNum(row['vehiclesExpected']),
    oldestFixAgeSec: optNum(row['oldestFixAgeSec']),
  }));
  return { available: true, value };
}

function normalizeVehicles(raw: unknown): Maybe<NetworkVehicle[]> {
  const { ok, reason, body } = unwrapAvailability(raw);
  if (!ok) return { available: false, reason };
  const source = isRecord(body) ? arr(body['vehicles'] ?? body['rows'] ?? body['items']) : arr(body);
  const value: NetworkVehicle[] = source.filter(isRecord).map((row, i) => ({
    id: str(row['id'] ?? row['vehicleId'], `veh-${i}`),
    lineNo: str(row['lineNo'], '?'),
    fixAgeSec: num(row['fixAgeSec'], Number.NaN),
    lat: optNum(row['lat'] ?? row['latitude']),
    lon: optNum(row['lon'] ?? row['longitude']),
    speedKmh: optNum(row['speedKmh'] ?? row['speed']),
  }));
  return { available: true, value };
}

function normalizeLenzHealth(raw: unknown): Maybe<LenzHealth> {
  const { ok, reason, body } = unwrapAvailability(raw);
  if (!ok) return { available: false, reason };
  if (!isRecord(body)) return { available: false, reason: 'Réponse de santé illisible' };
  const status = optStr(body['status'] ?? body['lenz']);
  const reachable =
    typeof body['reachable'] === 'boolean' ? body['reachable'] : status !== 'down' && status !== undefined;
  return {
    available: true,
    value: {
      reachable,
      latencyMs: optNum(body['latencyMs'] ?? body['latency']),
      lastSyncAt: optStr(body['lastSyncAt'] ?? body['lastSync']),
      status,
    },
  };
}

function normalizeIncidents(raw: unknown): Incident[] {
  const source = isRecord(raw) ? arr(raw['incidents'] ?? raw['rows'] ?? raw['items']) : arr(raw);
  return source.filter(isRecord).map((row, i) => ({
    id: str(row['id'], `inc-${i}`),
    source: str(row['source'], 'app'),
    category: str(row['category'], 'autre'),
    lineNo: optStr(row['lineNo']) ?? null,
    severity: str(row['severity'], 'low'),
    status: str(row['status'], 'open'),
    createdAt: str(row['createdAt']),
    resolvedAt: optStr(row['resolvedAt']) ?? null,
    note: optStr(row['note']) ?? null,
  }));
}

function normalizeAlerts(raw: unknown): Alert[] {
  const source = isRecord(raw) ? arr(raw['items'] ?? raw['alerts'] ?? raw['rows']) : arr(raw);
  return source.filter(isRecord).map((row, i) => ({
    id: str(row['id'], `alert-${i}`),
    rule: str(row['rule'], 'inconnue'),
    severity: str(row['severity'], 'warning'),
    // Repli sur l'identifiant seulement si le serveur n'a pas rédigé de titre —
    // ce qui ne devrait jamais arriver, mais vaut mieux qu'une ligne vide.
    title: str(row['title'], str(row['rule'], 'Alerte')),
    detail: str(row['detail']),
    openedAt: str(row['openedAt']),
    resolvedAt: optStr(row['resolvedAt']) ?? null,
    acknowledgedAt: optStr(row['acknowledgedAt']) ?? null,
    acknowledgedBy: optStr(row['acknowledgedBy']) ?? null,
    lastNotifiedAt: optStr(row['lastNotifiedAt']) ?? null,
  }));
}

function normalizeReportSummary(row: Record<string, unknown>, i: number): ReportSummary {
  return {
    id: str(row['id'], `rep-${i}`),
    kind: str(row['kind'], 'daily'),
    periodStart: str(row['periodStart']),
    periodEnd: str(row['periodEnd']),
    generatedAt: str(row['generatedAt']),
  };
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

export interface RangeParams {
  from?: string;
  to?: string;
}

/**
 * Normalise la réponse d'authentification.
 *
 * Défensif comme le reste du fichier : si le serveur renvoyait un rôle inconnu
 * (déploiements décalés), on retombe sur `viewer` plutôt que d'accorder des
 * droits qu'on n'a pas su lire. Le serveur reste de toute façon l'autorité —
 * ce rôle-ci ne sert QU'À l'affichage, jamais à autoriser quoi que ce soit.
 */
function normalizeSession(raw: unknown): SessionInfo {
  const root = isRecord(raw) ? raw : {};
  const op = isRecord(root['operator']) ? root['operator'] : {};
  const roleRaw = str(op['role']);
  const role: Role = (ROLES as readonly string[]).includes(roleRaw) ? (roleRaw as Role) : 'viewer';
  const permissions = arr(root['permissions']).filter(
    (p): p is Permission => typeof p === 'string' && (PERMISSIONS as readonly string[]).includes(p),
  );
  return {
    operator: {
      id: str(op['id']),
      username: str(op['username'], 'inconnu'),
      displayName: str(op['displayName'], str(op['username'], 'inconnu')),
      role,
    },
    permissions,
  };
}

// ─── Départs vers l'aéroport (2026-10-07) ───────────────────────────────────
// Contrat : architecture-departs-aeroport-2026-10-07.md § 3.2. Les colonnes
// sensibles (organisation, effectif) sont visibles ICI, entre opérateurs ; elles
// ne sont jamais dans le plan envoyé à l'application.

export type DepartureFileStatus = 'draft' | 'published' | 'superseded' | 'rejected';
export type DepartureMode = 'direct_bus' | 'shuttle_ter' | 'line_ter';

export interface DepartureFileSummary {
  id: string;
  receivedAt: string;
  fileName: string;
  status: DepartureFileStatus;
  publishedAt: string | null;
  pushedAt: string | null;
  pushError: string | null;
  coveredDates: string[];
  rowCount: number;
  uploadedBy: { id: string; displayName: string };
  publishedBy: { id: string; displayName: string } | null;
}

export interface DepartureColumnReport {
  header: string;
  field: string | null;
  sensitive: boolean;
}

export interface DepartureParseReport {
  sheets: { name: string; headerRow: number | null; rowsRead: number }[];
  columns: DepartureColumnReport[];
  missingColumns: string[];
  rowsRead: number;
  rowsKept: number;
  rejectedRows: { sheet: string; rowNumber: number; reason: string }[];
  duplicates: number;
  coveredDates: string[];
  flightDates: string[];
}

export interface DepartureUnknownHotel {
  label: string;
  key: string;
  count: number;
}

export interface DepartureRow {
  id: string;
  rowNumber: number;
  mode: DepartureMode;
  busNumber: string | null;
  lineNos: string[];
  boardingPlace: string;
  boardingDate: string;
  boardingTime: string;
  terTime: string | null;
  station: string | null;
  hotelLabel: string;
  hotelKey: string;
  hotel: { id: string; name: string } | null;
  flightNumber: string;
  flightDate: string;
  flightTime: string;
  arrivalTime: string | null;
  organization: string | null;
  personCount: number | null;
}

export interface DepartureFileDetail {
  file: DepartureFileSummary;
  report: DepartureParseReport;
  unknownHotels: DepartureUnknownHotel[];
  canPublish: boolean;
  rows: DepartureRow[];
}

export interface DepartureHotel {
  id: string;
  name: string;
  station: string | null;
  zone: string | null;
  address: string | null;
  isBoardingPlaceOnly: boolean;
  active: boolean;
  aliases: string[];
  rowCount: number;
}

export interface DepartureCurrent {
  files: DepartureFileSummary[];
  /** Par journée en ligne : le fichier qui la porte et le nombre de transferts de CETTE journée. */
  byDate: Record<string, DepartureFileSummary & { dayRowCount: number }>;
  hotelCount: number;
  pushConfigured: boolean;
}

export interface DeparturePush {
  ok: boolean;
  skipped?: boolean;
  error?: string;
}

export interface DeparturePublishResult {
  fileId: string;
  publishedAt: string;
  coveredDates: string[];
  supersededFileIds: string[];
  push: DeparturePush;
}

export interface DepartureUnpublishResult {
  fileId: string;
  /** Journées que l'application continue de servir, prises dans les autres fichiers publiés. */
  remainingDates: string[];
  push: DeparturePush;
}

export interface DepartureColumnField {
  field: string;
  label: string;
  required: boolean;
  sensitive: boolean;
  synonyms: string[];
}

const DEPARTURE_STATUSES: DepartureFileStatus[] = ['draft', 'published', 'superseded', 'rejected'];

function normalizeDepartureFile(raw: unknown): DepartureFileSummary {
  const r = isRecord(raw) ? raw : {};
  const status = str(r['status'], 'draft');
  const uploadedBy = isRecord(r['uploadedBy']) ? r['uploadedBy'] : {};
  const publishedBy = isRecord(r['publishedBy']) ? r['publishedBy'] : null;
  return {
    id: str(r['id']),
    receivedAt: str(r['receivedAt']),
    fileName: str(r['fileName'], 'fichier.xlsx'),
    status: (DEPARTURE_STATUSES as string[]).includes(status) ? (status as DepartureFileStatus) : 'draft',
    publishedAt: optStr(r['publishedAt']) ?? null,
    pushedAt: optStr(r['pushedAt']) ?? null,
    pushError: optStr(r['pushError']) ?? null,
    coveredDates: arr(r['coveredDates']).filter((d): d is string => typeof d === 'string'),
    rowCount: num(r['rowCount']),
    uploadedBy: { id: str(uploadedBy['id']), displayName: str(uploadedBy['displayName'], 'inconnu') },
    publishedBy: publishedBy ? { id: str(publishedBy['id']), displayName: str(publishedBy['displayName'], 'inconnu') } : null,
  };
}

/**
 * Téléversement : un `FormData`, donc PAS le `request()` générique (qui pose un
 * `content-type: application/json`). Même cookie, mêmes erreurs Problem Details.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path), { method: 'POST', body: form, credentials: 'include', headers: { accept: 'application/json', [DASHBOARD_HEADER]: '1' } });
  } catch (cause) {
    throw new ApiError(0, { title: 'API injoignable', detail: String(cause) }, true);
  }
  if (!res.ok) {
    const { problem, parsed } = await toProblem(res);
    if (res.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    throw new ApiError(res.status, problem, !parsed && res.status >= 500);
  }
  return (await res.json()) as T;
}

// ─── Annonces (2026-10-22) ──────────────────────────────────────────────────

export type AnnouncementTarget = { kind: 'all' } | { kind: 'lines'; lineNos: string[] };

export interface AnnouncementText {
  title: string;
  body: string;
}

export interface AnnouncementDraft {
  target: AnnouncementTarget;
  fr: AnnouncementText;
  en: AnnouncementText;
}

export interface AnnouncementRecipients {
  ios: number;
  android: number;
  total: number;
}

export interface AnnouncementItem extends AnnouncementDraft {
  id: string;
  createdAt: string;
  sentBy: string;
  status: 'sending' | 'sent' | 'failed';
  recipients: number;
  sent: number;
  failed: number;
  androidPending: number;
  error: string | null;
  sentAt: string | null;
}

export interface AnnouncementsPage {
  configured: boolean;
  limits: { title: number; body: number };
  lines: { lineNo: string; name: string; cluster: 'dakar' | 'diamniadio' | 'saly' | null }[];
  items: AnnouncementItem[];
}

export const api = {
  health: (signal?: AbortSignal) => request<Health>('/health', { signal }),

  // ─── Authentification ─────────────────────────────────────────────────────

  login: (username: string, password: string): Promise<SessionInfo> =>
    request<unknown>('/auth/login', {
      method: 'POST',
      body: { username, password },
      silent401: true,
    }).then(normalizeSession),

  logout: (): Promise<void> => request<void>('/auth/logout', { method: 'POST', silent401: true }),

  /** Interrogé au chargement : un 401 est une réponse normale, pas une anomalie. */
  me: (signal?: AbortSignal): Promise<SessionInfo> =>
    request<unknown>('/auth/me', { signal, silent401: true }).then(normalizeSession),

  overview: (range: RangeParams, signal?: AbortSignal) =>
    request<Overview>('/metrics/overview', { query: { from: range.from, to: range.to }, signal }),

  timeseries: (
    metric: TimeseriesMetric,
    range: RangeParams,
    bucket: Bucket,
    signal?: AbortSignal,
  ): Promise<Timeseries> =>
    request<Timeseries>('/metrics/timeseries', {
      query: { metric, from: range.from, to: range.to, bucket },
      signal,
    }).then((res) => ({
      metric,
      bucket,
      // Le serveur peut renvoyer une série vide ; on garantit juste le tableau.
      points: arr(res?.points).filter(isRecord).map((p) => ({ t: str(p['t']), v: num(p['v']) })),
    })),

  breakdown: (dimension: BreakdownDimension, range: RangeParams, signal?: AbortSignal): Promise<Breakdown> =>
    request<Breakdown>('/metrics/breakdown', {
      query: { dimension, from: range.from, to: range.to },
      signal,
    }).then((res) => ({
      dimension,
      rows: arr(res?.rows)
        .filter(isRecord)
        .map((r) => ({
          key: str(r['key'], 'autre'),
          label: str(r['label'], str(r['key'], 'autre')),
          value: num(r['value']),
          share: num(r['share']),
        })),
    })),

  funnel: (range: RangeParams, signal?: AbortSignal): Promise<Funnel> =>
    request<unknown>('/metrics/funnel', { query: { from: range.from, to: range.to }, signal }).then(
      normalizeFunnel,
    ),

  networkLines: (signal?: AbortSignal): Promise<Maybe<NetworkLine[]>> =>
    request<unknown>('/network/lines', { signal }).then(normalizeLines),

  networkVehicles: (signal?: AbortSignal): Promise<Maybe<NetworkVehicle[]>> =>
    request<unknown>('/network/vehicles', { signal }).then(normalizeVehicles),

  networkHealth: (signal?: AbortSignal): Promise<Maybe<LenzHealth>> =>
    request<unknown>('/network/health', { signal }).then(normalizeLenzHealth),

  incidents: (
    filters: { status?: string; from?: string; to?: string },
    signal?: AbortSignal,
  ): Promise<Incident[]> =>
    request<unknown>('/incidents', {
      query: { status: filters.status, from: filters.from, to: filters.to },
      signal,
    }).then(normalizeIncidents),

  patchIncident: (
    id: string,
    patch: { status?: string; severity?: string; note?: string },
  ): Promise<Incident> =>
    request<unknown>(`/incidents/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }).then(
      (raw) => {
        const rows = normalizeIncidents(Array.isArray(raw) ? raw : [raw]);
        const first = rows[0];
        if (!first) throw new ApiError(0, { title: 'Réponse de mise à jour vide' });
        return first;
      },
    ),

  /** `status=open` par défaut : ce qui est cassé MAINTENANT. */
  alerts: (status: 'open' | 'all' = 'open', signal?: AbortSignal): Promise<Alert[]> =>
    request<unknown>('/alerts', { query: { status }, signal }).then(normalizeAlerts),

  acknowledgeAlert: (id: string): Promise<Alert> =>
    request<unknown>(`/alerts/${encodeURIComponent(id)}/acknowledge`, { method: 'POST' }).then((raw) => {
      const rows = normalizeAlerts(Array.isArray(raw) ? raw : [raw]);
      const first = rows[0];
      if (!first) throw new ApiError(0, { title: 'Réponse d’acquittement vide' });
      return first;
    }),

  reports: (kind: string, limit: number, signal?: AbortSignal): Promise<ReportSummary[]> =>
    request<unknown>('/reports', { query: { kind, limit }, signal }).then((raw) => {
      const source = isRecord(raw) ? arr(raw['reports'] ?? raw['rows'] ?? raw['items']) : arr(raw);
      return source.filter(isRecord).map(normalizeReportSummary);
    }),

  generateReport: (kind: string, date: string): Promise<ReportSummary> =>
    request<unknown>('/reports/generate', { method: 'POST', body: { kind, date } }).then((raw) => {
      if (!isRecord(raw)) throw new ApiError(0, { title: 'Réponse de génération illisible' });
      const inner = isRecord(raw['report']) ? raw['report'] : raw;
      return normalizeReportSummary(inner, 0);
    }),

  report: (id: string, signal?: AbortSignal): Promise<ReportDetail> =>
    request<unknown>(`/reports/${encodeURIComponent(id)}`, { signal }).then((raw) => {
      if (!isRecord(raw)) throw new ApiError(0, { title: 'Rapport illisible' });
      const inner = isRecord(raw['report']) ? raw['report'] : raw;
      return {
        ...normalizeReportSummary(inner, 0),
        summaryMd: str(inner['summaryMd']),
        payload: inner['payload'] ?? null,
      };
    }),

  /** Exports texte : on récupère le contenu pour le proposer en téléchargement. */
  reportExport: (id: string, format: 'md' | 'csv', signal?: AbortSignal): Promise<string> =>
    request<string>(`/reports/${encodeURIComponent(id)}.${format}`, { as: 'text', signal }),
  // ─── Départs vers l'aéroport ───────────────────────────────────────────────

  departuresCurrent: (signal?: AbortSignal): Promise<DepartureCurrent> =>
    request<unknown>('/departures/current', { signal }).then((raw) => {
      const r = isRecord(raw) ? raw : {};
      const byDateRaw = isRecord(r['byDate']) ? r['byDate'] : {};
      const byDate: Record<string, DepartureFileSummary & { dayRowCount: number }> = {};
      for (const [date, file] of Object.entries(byDateRaw)) {
        const summary = normalizeDepartureFile(file);
        const dayRowCount = isRecord(file) && typeof file['dayRowCount'] === 'number' ? file['dayRowCount'] : summary.rowCount;
        byDate[date] = { ...summary, dayRowCount };
      }
      return {
        files: arr(r['files']).map(normalizeDepartureFile),
        byDate,
        hotelCount: num(r['hotelCount']),
        pushConfigured: r['pushConfigured'] === true,
      };
    }),

  departureFiles: (limit = 50, signal?: AbortSignal): Promise<DepartureFileSummary[]> =>
    request<unknown>('/departures/files', { query: { limit }, signal }).then((raw) =>
      arr(isRecord(raw) ? raw['items'] : raw).map(normalizeDepartureFile),
    ),

  departureFile: (id: string, signal?: AbortSignal): Promise<DepartureFileDetail> =>
    request<DepartureFileDetail>(`/departures/files/${encodeURIComponent(id)}`, { signal }).then((raw) => ({
      ...raw,
      file: normalizeDepartureFile(raw.file),
      unknownHotels: arr(raw.unknownHotels) as DepartureUnknownHotel[],
      rows: arr(raw.rows) as DepartureRow[],
    })),

  uploadDepartureFile: (file: File): Promise<{ file: DepartureFileSummary; report: DepartureParseReport; unknownHotels: DepartureUnknownHotel[] }> => {
    const form = new FormData();
    form.append('file', file, file.name);
    return upload<{ file: unknown; report: DepartureParseReport; unknownHotels: DepartureUnknownHotel[] }>('/departures/files', form).then((raw) => ({
      file: normalizeDepartureFile(raw.file),
      report: raw.report,
      unknownHotels: arr(raw.unknownHotels) as DepartureUnknownHotel[],
    }));
  },

  publishDepartureFile: (id: string): Promise<DeparturePublishResult> =>
    request<DeparturePublishResult>(`/departures/files/${encodeURIComponent(id)}/publish`, { method: 'POST' }),

  republishDepartureFile: (id: string): Promise<DeparturePublishResult> =>
    request<DeparturePublishResult>(`/departures/files/${encodeURIComponent(id)}/republish`, { method: 'POST' }),

  pushDepartureFile: (id: string): Promise<{ fileId: string; push: DeparturePush }> =>
    request<{ fileId: string; push: DeparturePush }>(`/departures/files/${encodeURIComponent(id)}/push`, { method: 'POST' }),

  /** Retirer un fichier publié de l'application : il redevient brouillon, le plan restant (ou vide) repart. 409 si l'application n'a pas pu être mise à jour. */
  unpublishDepartureFile: (id: string): Promise<DepartureUnpublishResult> =>
    request<DepartureUnpublishResult>(`/departures/files/${encodeURIComponent(id)}/unpublish`, { method: 'POST' }),

  /** Retirer un brouillon ou un fichier refusé. Le serveur refuse (409) pour un fichier publié ou remplacé. */
  announcements: (signal?: AbortSignal): Promise<AnnouncementsPage> => request<AnnouncementsPage>('/announcements', { signal }),

  previewAnnouncement: (draft: AnnouncementDraft): Promise<{ recipients: AnnouncementRecipients; apnsConfigured: boolean }> =>
    request<{ recipients: AnnouncementRecipients; apnsConfigured: boolean }>('/announcements/preview', { method: 'POST', body: draft }),

  sendAnnouncement: (
    draft: AnnouncementDraft,
  ): Promise<{ id: string; recipients: AnnouncementRecipients; sent: number; failed: number; androidPending: number }> =>
    request<{ id: string; recipients: AnnouncementRecipients; sent: number; failed: number; androidPending: number }>('/announcements', {
      method: 'POST',
      body: draft,
    }),

  deleteDepartureFile: (id: string): Promise<{ fileId: string; status: DepartureFileStatus }> =>
    request<{ fileId: string; status: DepartureFileStatus }>(`/departures/files/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  departureHotels: (signal?: AbortSignal): Promise<DepartureHotel[]> =>
    request<unknown>('/departures/hotels', { signal }).then((raw) => arr(isRecord(raw) ? raw['items'] : raw) as DepartureHotel[]),

  createDepartureHotel: (body: { name: string; station?: string | null; zone?: string | null; address?: string | null; isBoardingPlaceOnly?: boolean; aliases?: string[] }): Promise<{ id: string; resolvedRows: number }> =>
    request<{ id: string; resolvedRows: number }>('/departures/hotels', { method: 'POST', body }),

  patchDepartureHotel: (
    id: string,
    body: { name?: string; station?: string | null; zone?: string | null; address?: string | null; isBoardingPlaceOnly?: boolean; active?: boolean },
  ): Promise<{ push: DeparturePush | null }> =>
    request<{ push: DeparturePush | null }>(`/departures/hotels/${encodeURIComponent(id)}`, { method: 'PATCH', body }),

  addDepartureHotelAlias: (id: string, label: string): Promise<{ resolvedRows: number }> =>
    request<{ resolvedRows: number }>(`/departures/hotels/${encodeURIComponent(id)}/aliases`, { method: 'POST', body: { label } }),

  // ─── Comptes d'exploitation ──────────────────────────────────────────────

  operators: (signal?: AbortSignal): Promise<OperatorAccount[]> =>
    request<unknown>('/operators', { signal }).then((raw) => arr(isRecord(raw) ? raw['items'] : raw) as OperatorAccount[]),

  createOperator: (body: { username: string; displayName: string; password: string; role: Role }): Promise<OperatorAccount> =>
    request<OperatorAccount>('/operators', { method: 'POST', body }),

  patchOperator: (id: string, body: { displayName?: string; role?: Role; password?: string; disabled?: boolean }): Promise<OperatorAccount> =>
    request<OperatorAccount>(`/operators/${encodeURIComponent(id)}`, { method: 'PATCH', body }),

  revokeOperatorSessions: (id: string): Promise<{ revoked: number }> =>
    request<{ revoked: number }>(`/operators/${encodeURIComponent(id)}/revoke-sessions`, { method: 'POST' }),

  departureColumns: (signal?: AbortSignal): Promise<DepartureColumnField[]> =>
    request<unknown>('/departures/columns', { signal }).then((raw) => arr(isRecord(raw) ? raw['fields'] : raw) as DepartureColumnField[]),

  addDepartureColumnSynonym: (field: string, header: string): Promise<unknown> =>
    request<unknown>('/departures/columns', { method: 'POST', body: { field, header } }),
};
